import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Spinner, ArrowLeft } from '@phosphor-icons/react';

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'polling', payment_status: '', purpose: '', ref_id: '', amount: 0 });
  const [error, setError] = useState(null);
  const [tries, setTries] = useState(0);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Keine Session-ID gefunden');
      return;
    }
    let cancelled = false;
    const poll = async (attempt) => {
      if (cancelled || stopRef.current) return;
      if (attempt > 20) {
        setState((s) => ({ ...s, status: 'timeout' }));
        return;
      }
      try {
        const res = await paymentApi.statusExtended(sessionId);
        const d = res.data;
        setState({
          status: d.status,
          payment_status: d.payment_status,
          purpose: d.purpose,
          ref_id: d.ref_id,
          amount: (d.amount_total || 0) / 100,
        });
        setTries(attempt);
        if (d.status === 'paid' || d.status === 'expired') {
          stopRef.current = true;
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setError(formatApiError(e));
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId]);

  const back = () => {
    if (state.purpose === 'invoice') navigate('/invoices');
    else if (state.purpose === 'deposit' && state.ref_id) navigate(`/bookings/${state.ref_id}`);
    else navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F7F8FA]">
      <div className="card-swiss p-8 max-w-md w-full text-center" data-testid="payment-success-page">
        {error ? (
          <>
            <XCircle weight="fill" size={64} className="text-[#E60000] mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Fehler</h2>
            <p className="text-sm text-[#525866] mb-6">{error}</p>
            <Button onClick={back} className="btn-primary"><ArrowLeft size={14} className="mr-1" /> Zurück</Button>
          </>
        ) : state.status === 'paid' ? (
          <>
            <CheckCircle weight="fill" size={64} className="text-[#009951] mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Zahlung erfolgreich</h2>
            <p className="text-[#525866] mb-2">
              {state.purpose === 'invoice' && 'Rechnung wurde online bezahlt.'}
              {state.purpose === 'deposit' && 'Kaution wurde online hinterlegt.'}
              {state.purpose === 'inquiry' && 'Anfrage-Zahlung erfolgreich.'}
            </p>
            <p className="text-2xl font-bold text-[#009951] mb-6">€ {state.amount.toFixed(2)}</p>
            <p className="text-xs text-[#8A94A6] mb-6">
              Hinweis: Die Zahlung muss manuell im Stripe-Dashboard verifiziert und in der App auf „bezahlt" gesetzt werden.
            </p>
            <Button onClick={back} className="btn-primary" data-testid="back-after-success">
              <ArrowLeft size={14} className="mr-1" /> Zurück zur App
            </Button>
          </>
        ) : state.status === 'expired' ? (
          <>
            <XCircle weight="fill" size={64} className="text-[#E60000] mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Sitzung abgelaufen</h2>
            <p className="text-sm text-[#525866] mb-6">Die Zahlung wurde nicht abgeschlossen. Bitte erneut starten.</p>
            <Button onClick={back} className="btn-primary"><ArrowLeft size={14} className="mr-1" /> Zurück</Button>
          </>
        ) : state.status === 'timeout' ? (
          <>
            <XCircle weight="fill" size={64} className="text-[#B25A00] mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Zeitüberschreitung</h2>
            <p className="text-sm text-[#525866] mb-6">
              Status konnte nicht ermittelt werden. Bitte prüfe Stripe-Dashboard direkt.
            </p>
            <Button onClick={back} className="btn-primary"><ArrowLeft size={14} className="mr-1" /> Zurück</Button>
          </>
        ) : (
          <>
            <Spinner size={64} className="text-[#525866] mx-auto mb-3 animate-spin" />
            <h2 className="text-xl font-bold mb-2">Zahlung wird verarbeitet…</h2>
            <p className="text-sm text-[#8A94A6]">Versuch {tries}/20 — bitte einen Moment Geduld</p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
